import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { Bot, Zap, ShieldAlert, AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const fmt = (n) => (n ?? 0).toLocaleString();

export default function SAAiGateway() {
  const { tenantId } = useSuperAdminTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/ai-gateway", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const items = data?.items || [];
  const totals = items.reduce((acc, i) => ({
    calls: acc.calls + (i.calls_30d || 0),
    blocked: acc.blocked + (i.blocked_30d || 0),
    errors: acc.errors + (i.errors_30d || 0),
    enabled: acc.enabled + (i.enabled ? 1 : 0),
  }), { calls: 0, blocked: 0, errors: 0, enabled: 0 });

  return (
    <SuperAdminShell title="AI Gateway">
      <div className="px-5 md:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Tenants with AI on" value={`${totals.enabled}/${items.length}`} icon={Bot} accent="#22D3EE" />
          <Kpi label="Calls (30d)" value={fmt(totals.calls)} icon={Zap} accent="#FBBF24" />
          <Kpi label="Blocked (30d)" value={fmt(totals.blocked)} icon={ShieldAlert} accent="#F87171" />
          <Kpi label="Errors (30d)" value={fmt(totals.errors)} icon={AlertTriangle} accent="#A78BFA" />
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm" data-testid="sa-ai-gateway-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Blocked terms</th>
                <th className="px-4 py-3 text-right">Calls (30d)</th>
                <th className="px-4 py-3 text-right">Blocked</th>
                <th className="px-4 py-3 text-right">Errors</th>
                <th className="px-4 py-3 text-right">Avg latency</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={7} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No tenants in scope</td></tr>
              ) : items.map((i) => (
                <tr key={i.tenant_id} data-testid="sa-ai-gateway-row" className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white">{i.tenant_name}</td>
                  <td className="px-4 py-2">
                    <Badge className={`text-[10px] border-0 ${i.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"}`}>
                      {i.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                    {!i.log_prompts && <span className="ml-1.5 text-[10px] text-white/40">no logging</span>}
                  </td>
                  <td className="px-4 py-2 text-white/70">{fmt(i.blocked_terms_count)}</td>
                  <td className="px-4 py-2 text-right text-white/80">{fmt(i.calls_30d)}</td>
                  <td className="px-4 py-2 text-right">{i.blocked_30d > 0 ? <span className="text-amber-300">{fmt(i.blocked_30d)}</span> : <span className="text-white/40">0</span>}</td>
                  <td className="px-4 py-2 text-right">{i.errors_30d > 0 ? <span className="text-red-300">{fmt(i.errors_30d)}</span> : <span className="text-white/40">0</span>}</td>
                  <td className="px-4 py-2 text-right text-white/60">{i.avg_latency_ms ? `${i.avg_latency_ms} ms` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-white/40">Read-only oversight — settings and full request logs are managed per-tenant in each tenant&apos;s Admin Console → AI Gateway.</p>
      </div>
    </SuperAdminShell>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="mt-3 text-2xl font-display text-white">{value}</div>
    </div>
  );
}
